import { useSelector } from '@/api/state';
import { getScheduledEventsByTaskId, getTask } from '@/utils/stateAccessors';
import { Button } from '@payaca/ambrosia';
import { HeaderTagType } from '@payaca/components/headerTag/HeaderTag';
import MiniLoader from '@payaca/components/miniLoader/MiniLoader';
import {
  getContactsToNotify,
  groupScheduledEventsByDate,
} from '@payaca/helpers/scheduledEventsHelper';
import { ScheduledEventsPermissions } from '@payaca/permissions/scheduledEvents/scheduled-events.permissions';
import { useCustomer, useDeal } from '@payaca/store/hooks/appState';
import * as taskActions from '@payaca/store/tasks/tasksActions';
import { ScheduledEvent } from '@payaca/types/scheduledEventsTypes';
import UntitledIcon from '@payaca/untitled-icons';
import { FC, useMemo, useState, type JSX } from 'react';
import { useDispatch } from 'react-redux';
import { CreateScheduledEventModal } from '../createScheduledEventModal/CreateScheduledEventModal';
import GroupedScheduledEventsList from '../groupedScheduledEventsList/GroupedScheduledEventsList';
import { PermissionGuard } from '../permissionGuard/PermissionGuard';
import ScheduledEventControl from '../scheduledEventControl/ScheduledEventControl';
import './TaskLinkedScheduledEventsPanel.sass';

type Props = {
  taskId: number;
};
const TaskLinkedScheduledEventsPanel: FC<Props> = ({
  taskId,
}: Props): JSX.Element | null => {
  const dispatch = useDispatch();
  const [showCreateScheduledEventModal, setShowCreateScheduledEventModal] =
    useState(false);

  const task = useSelector((state) => getTask(state, taskId));

  const deal = useDeal(task?.dealId);

  const customer = useCustomer(deal?.customerId);

  const scheduledEvents = useSelector((state) => {
    return getScheduledEventsByTaskId(state, taskId);
  });

  const groupedScheduledEvents = useMemo(() => {
    return groupScheduledEventsByDate(scheduledEvents);
  }, [scheduledEvents]);

  const isGettingScheduledEventsForTask = useSelector((state) => {
    return state.scheduledEvents.isGettingScheduledEventsForTask;
  });

  const newScheduledEventBase = useMemo(() => {
    const siteAddress = deal?.siteAddresses?.[0];

    return {
      dealId: task?.dealId,
      taskIds: task?.id ? [task?.id] : undefined,
      userAssignments: task?.assignedToUserId
        ? [task.assignedToUserId]
        : undefined,
      location: siteAddress
        ? {
            id: siteAddress.id,
            address: siteAddress.address,
            contacts: siteAddress.contacts,
          }
        : undefined,
      contactsToNotify: getContactsToNotify({
        customer,
        locationContacts: siteAddress?.contacts,
      }),
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- FIXME: pre-existing error: React Hook useMemo has missing dependencies: 'customer', and 'deal.siteAddresses'
  }, [task]);

  if (!task) return null;

  return (
    <div className="task-linked-scheduled-events-panel">
      {isGettingScheduledEventsForTask && !scheduledEvents?.length ? (
        <div className="loader-container">
          <MiniLoader />
        </div>
      ) : (
        <>
          <div className="flex space-x-3.5 items-center">
            <h2 className="flex space-x-3.5 items-center grow">
              <span className="scheduled-events-icon-container">
                <UntitledIcon name="calendar" />
              </span>
              {scheduledEvents.length} linked{' '}
              {scheduledEvents.length === 1 ? 'event' : 'events'}
            </h2>
            <PermissionGuard
              renderIfHasPermissions={[ScheduledEventsPermissions.ADD_EVENT]}
            >
              <Button
                aria-label="Add scheduled event"
                variant="secondary"
                size="xs"
                onClick={() => setShowCreateScheduledEventModal(true)}
              >
                <UntitledIcon name="plus.3" className="h-3.5 w-3.5" />
              </Button>
            </PermissionGuard>
          </div>
          {!!scheduledEvents?.length && (
            <div>
              <GroupedScheduledEventsList
                groupHeaderTagType={HeaderTagType.H3}
                groupedScheduledEvents={groupedScheduledEvents.filter(
                  (x) => x.groupName !== 'Past'
                )}
                renderScheduledEvent={(scheduledEvent: ScheduledEvent) => {
                  return (
                    <ScheduledEventControl
                      scheduledEventId={scheduledEvent.id}
                      hideQuickActions={false}
                      disableShowReadDrawer={true}
                      onPersistScheduledEventSuccess={() =>
                        dispatch(taskActions.requestGetTask(taskId))
                      }
                    />
                  );
                }}
              />
              <GroupedScheduledEventsList
                groupedScheduledEvents={groupedScheduledEvents.filter(
                  (x) => x.groupName == 'Past'
                )}
                renderScheduledEvent={(scheduledEvent: ScheduledEvent) => {
                  return (
                    <ScheduledEventControl
                      scheduledEventId={scheduledEvent.id}
                      hideQuickActions={false}
                      disableShowReadDrawer={true}
                      onPersistScheduledEventSuccess={() =>
                        dispatch(taskActions.requestGetTask(taskId))
                      }
                    />
                  );
                }}
              />
            </div>
          )}
        </>
      )}
      <CreateScheduledEventModal
        isOpen={showCreateScheduledEventModal}
        onClose={() => setShowCreateScheduledEventModal(false)}
        hiddenFields={
          task?.dealId ? ['customerIds'] : ['customerIds', 'dealId']
        }
        disabledFields={['dealId']}
        initialEventData={newScheduledEventBase}
        onSuccess={() => {
          dispatch(taskActions.requestGetTask(taskId));
          setShowCreateScheduledEventModal(false);
        }}
      />
    </div>
  );
};
export default TaskLinkedScheduledEventsPanel;
